// Definition: Helper functions for localization

import React from "react";
import {
  ChildProps,
  FieldData,
  FormValue,
  GenericData,
  Validation,
} from "./types";
import { z } from "zod";
import { isValidType, ValidTypes } from "./ValidTypes";
import dayjs from "dayjs";

export const omitProperties = (obj: any, keys: string[]) => {
  const { [keys[0]]: _, ...rest } = obj;
  return rest;
};

export const getAllObjectsAndArrays = (obj: any) => {
  let result: any[] = [];

  const traverse = (currentObj: any) => {
    if (typeof currentObj === "object" && currentObj !== null) {
      result.push(currentObj);
      for (const key in currentObj) {
        if (currentObj.hasOwnProperty(key)) {
          traverse(currentObj[key]);
        }
      }
    }
  };

  traverse(obj);
  return result[0];
};

export const prepareContent = (
  value: any | null,
  fieldAlias: string,
  nonForm?: boolean
) => {
  if (nonForm) {
    return {
      [fieldAlias]: value,
    } as GenericData;
  }
  return { fieldAlias, value } as FormValue;
};

const getAttributesFromChildren = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const fieldProps: FieldData[] = [];

  const traverseChildren = (children: React.ReactNode) => {
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const childProps = child.props.children as
          | React.ReactElement<ChildProps>
          | React.ReactElement<ChildProps>[];

        if (Array.isArray(childProps)) {
          childProps.forEach((singleChildProps) => {
            if (React.isValidElement(singleChildProps)) {
              if (
                isValidType(
                  (singleChildProps.type as any).fieldType as ValidTypes
                )
              ) {
                if (!singleChildProps.props.excludeFromData) {
                  const validations = [
                    singleChildProps.props.validations,
                    singleChildProps.props.required
                      ? { isNotNullOrEmpty: singleChildProps.props.alias }
                      : null,
                  ]
                    .filter(Boolean)
                    .flat();
                  fieldProps.push({
                    alias: singleChildProps.props.alias,
                    type: (singleChildProps.type as any)
                      .fieldType as ValidTypes,
                    validations: (validations as Validation[]) || [],
                    ...(singleChildProps.props.fieldOptions &&
                    singleChildProps.props.fieldOptions.length > 0
                      ? {
                          fieldOptions: singleChildProps.props.fieldOptions,
                          valueProp: singleChildProps.props.valueProp,
                        }
                      : {}),
                  });
                }
              }
              traverseChildren(singleChildProps.props.children);
            }
          });
          traverseChildren(childProps);
        } else if (React.isValidElement(childProps)) {
          const { props: singleChildProps } = childProps;
          if (isValidType((childProps.type as any).fieldType as ValidTypes)) {
            if (!singleChildProps.excludeFromData) {
              const validations = [
                singleChildProps.validations,
                singleChildProps.required
                  ? { isNotNullOrEmpty: singleChildProps.alias }
                  : null,
              ]
                .filter(Boolean)
                .flat();
              fieldProps.push({
                alias: singleChildProps.alias,
                type: (childProps.type as any).fieldType as ValidTypes,
                validations: (validations as Validation[]) || [],
                ...(singleChildProps.fieldOptions &&
                singleChildProps.fieldOptions.length > 0
                  ? {
                      fieldOptions: singleChildProps.fieldOptions,
                      valueProp: singleChildProps.valueProp,
                    }
                  : {}),
              });
            }
          }
          traverseChildren(singleChildProps.children);
        }
      }
    });
  };

  traverseChildren(children);
  return fieldProps;
};

const setDefaultData = (
  fieldAlias: string,
  value: any,
  nonForm?: boolean,
  initialValue?: any
) => {
  if (nonForm) {
    return {
      [fieldAlias]: value,
    } as GenericData;
  }
  if (initialValue) {
    return {
      fieldAlias,
      value: initialValue,
    } as FormValue;
  }
  return {
    fieldAlias,
    value,
  } as FormValue;
};

const getDefaultData = (
  alias: string,
  type: ValidTypes,
  validations: Validation[],
  nonForm?: boolean,
  initialValue?: any
) => {
  let required = false;
  validations.forEach((validation) => {
    if (validation.hasOwnProperty("isNotNullOrEmpty")) {
      required = true;
    }
  });

  switch (type) {
    case ValidTypes.CustomDatePicker:
    case ValidTypes.CustomDateTimePicker:
    case ValidTypes.CustomTimePicker:
      if (required) {
        return setDefaultData(
          alias,
          dayjs().format("YYYY-MM-DD"),
          nonForm,
          initialValue
        );
      }
      return setDefaultData(alias, null, nonForm, initialValue);
    case ValidTypes.CustomRadioGroup:
      return setDefaultData(alias, null, nonForm, initialValue);
    case ValidTypes.ValueSlider:
      return setDefaultData(alias, 50, nonForm, initialValue);
    case ValidTypes.CustomSelect:
      return setDefaultData(alias, "-1", nonForm, initialValue);
    case ValidTypes.CustomTextInput:
    case ValidTypes.CustomNumberInput:
      return setDefaultData(alias, "", nonForm, initialValue);
    case ValidTypes.CustomCheckbox:
    case ValidTypes.HiddenValueCalculator:
      return setDefaultData(alias, null, nonForm, initialValue);
    case ValidTypes.CustomCheckboxGroup:
      return setDefaultData(alias, [], nonForm, initialValue);
    case ValidTypes.ColorPicker: {
      return setDefaultData(alias, "#ffffff", nonForm, initialValue);
    }
  }
};

const mergeAndfilterData = <A extends z.ZodType<any, any, any>>(
  fieldDatas: FieldData[],
  data: z.infer<A>,
  initialValues: z.infer<A>
) => {
  const nonLocalizedData: FormValue[] = [];
  const nonFormData: GenericData[] = [];
  fieldDatas.forEach((fieldData) => {
    const { alias, type, validations } = fieldData;
    if (!Array.isArray(data?.values)) {
      const defaultData = getDefaultData(alias, type, validations, true);
      const existingNonLocalizedData = data?.[alias] as GenericData;
      if (defaultData && !existingNonLocalizedData) {
        nonFormData.push(defaultData as GenericData);
        return;
      }
      nonFormData.push({
        [alias]: existingNonLocalizedData,
      } as GenericData);
      return;
    } else {
      const initialValue = initialValues?.values.find(
        (a: any) => a.fieldAlias === alias
      )?.value;
      const defaultData = getDefaultData(
        alias,
        type,
        validations,
        false,
        initialValue
      );

      const existingNonLocalizedData = data.values?.find(
        (a: FormValue) => a.fieldAlias === alias
      ) as FormValue;
      if (defaultData && !existingNonLocalizedData) {
        nonLocalizedData.push(defaultData as FormValue);
        return;
      }
      nonLocalizedData.push(existingNonLocalizedData);
      return;
    }
  });

  return { nonLocalizedData, nonFormData };
};

export const populateFormData = <A extends z.ZodType<any, any, any>>(
  fieldDatas: FieldData[],
  data: z.infer<A>,
  initialValues?: z.infer<A>
): z.infer<A> => {
  const { nonLocalizedData, nonFormData } = mergeAndfilterData(
    fieldDatas,
    data,
    initialValues
  );
  if (nonLocalizedData?.length > 0) {
    const updatedData = nonLocalizedData?.map((field) => {
      const existingValue = data?.values.find(
        (a: FormValue) => a.fieldAlias === field.fieldAlias
      );

      return existingValue ? { ...field, value: existingValue.value } : field;
    }, {} as FormValue);
    return {
      formId: data.formId,
      values: [...updatedData],
    };
  } else {
    const mergedNonLocalizedData = nonFormData.reduce((acc, field) => {
      const exists = data?.hasOwnProperty(field.fieldAlias);
      const existingField = exists ? data[field.fieldAlias] : null;
      if (existingField) {
        const key = Object.keys(existingField)[0];
        const value = Object.values(existingField)[0];
        acc[key] = value;
      } else {
        const key = Object.keys(field)[0];
        const value = Object.values(field)[0];
        acc[key] = value;
      }
      return acc;
    }, {} as GenericData);
    return {
      ...mergedNonLocalizedData,
    };
  }
};

export const initializeFormData = <A extends z.ZodType<any, any, any>>(
  children: React.ReactElement,
  data: z.infer<A>,
  initialValues?: z.infer<A>
) => {
  const fieldDatas = getAttributesFromChildren({ children });
  const populatedData = populateFormData(fieldDatas, data, initialValues);
  return { populatedData, fieldDatas };
};
